import { create } from "zustand"
import { LogLeadTemp } from "../interfaces/LogLeadTemp"
import { objectStorage } from "../dbLog"
import { changeValueIfStringIntoNumber } from "../functions/general"

interface LogLeadTempStore {
    [key: string]: LogLeadTemp[]
}

interface LogLeadTempState {
    logLeadTemp: LogLeadTempStore
    isLoading: boolean
    error: string | null

    // CRUD Operations
    addLogLeadByTemplatePipelineId: (templatePipelineId: string, logLeadTemp: LogLeadTemp) => Promise<boolean>
    updateLogLeadTempByIdLog: (idLog: string, updatedLog: LogLeadTemp) => Promise<boolean>
    removeLogLeadTempByIdLog: (idLog: string) => Promise<boolean>
    removeLogLeadTempByLeadId: (leadId: string) => Promise<boolean>
    updateLogLeadTempKeyError: (id: string, error: string) => Promise<boolean>
    removeLogLeadTempByTemplatePipelineId: (templatePipelineId: string) => Promise<boolean>
    clearAllLogLeadTemp: () => Promise<boolean>

    // Getters
    getLogLeadTempByTemplatePipelineId: (templatePipelineId: string) => LogLeadTemp[]
    getLatestLogLeadTempByLeadId: (leadId: string) => LogLeadTemp | null
    getLengthLogSameLeadId: (leadId: string) => number
    getNominalLogSameLeadId: (leadId: string) => number
    getLogLeadTempByLeadId: (leadId: string) => LogLeadTemp | null
    getAllLogLeadTemp: () => LogLeadTempStore

    // Utility
    loadFromStorage: () => Promise<void>
    setLoading: (loading: boolean) => void
    setError: (error: string | null) => void
}

const saveToStorage = async (data: LogLeadTempStore): Promise<void> => {
    try {
        await objectStorage.setItem('log-lead-temp', data)
    } catch (error) {
        console.error('Error saving to storage:', error)
        throw error
    }
}

const loadFromStorageHelper = async (): Promise<LogLeadTempStore> => {
    try {
        const data = await objectStorage.getItem('log-lead-temp')
        return data || {}
    } catch (error) {
        console.error('Error loading from storage:', error)
        return {}
    }
}

const useLogLeadTempStore = create<LogLeadTempState>()((set, get) => ({
    logLeadTemp: {},
    isLoading: false,
    error: null,

    setLoading: (loading: boolean) => set({ isLoading: loading }),
    setError: (error: string | null) => set({ error }),

    loadFromStorage: async () => {
        try {
            set({ isLoading: true, error: null })
            const data = await loadFromStorageHelper()
            // console.log("Loaded data from storage:", data) // Debug log
            set({ logLeadTemp: data, isLoading: false })
        } catch (error) {
            console.error('Error loading from storage:', error)
            set({ error: 'Failed to load data from storage', isLoading: false })
        }
    },

    updateLogLeadTempKeyError: async (id: string, error: string): Promise<boolean> => {
        try {
            // Validation
            if (!id || typeof id !== 'string') {
                console.error('updateLogLeadTempKeyError: Invalid id parameter')
                set({ error: 'Invalid log ID provided' })
                return false
            }

            if (!error || typeof error !== 'string') {
                console.error('updateLogLeadTempKeyError: Invalid error parameter')
                set({ error: 'Invalid error message provided' })
                return false
            }

            const currentState = get().logLeadTemp
            const newLogLeadTemp: LogLeadTempStore = {}
            let logFound = false

            // More efficient approach - stop when found
            for (const templatePipelineId of Object.keys(currentState)) {
                const logs = currentState[templatePipelineId]
                const logIndex = logs.findIndex(log => log.id === id)
                
                if (logIndex !== -1) {
                    // Found the log, update it
                    newLogLeadTemp[templatePipelineId] = [
                        ...logs.slice(0, logIndex),
                        { ...logs[logIndex], keyError: error },
                        ...logs.slice(logIndex + 1)
                    ]
                    logFound = true
                    
                    // Copy other templates as-is
                    Object.keys(currentState).forEach(otherTemplateId => {
                        if (otherTemplateId !== templatePipelineId) {
                            newLogLeadTemp[otherTemplateId] = [...currentState[otherTemplateId]]
                        }
                    })
                    break
                } else {
                    // Copy as-is since no match found in this template
                    newLogLeadTemp[templatePipelineId] = [...logs]
                }
            }

            if (!logFound) {
                console.warn(`updateLogLeadTempKeyError: Log with id ${id} not found`)
                set({ error: `Log with id ${id} not found` })
                return false
            }

            // Save to storage
            await saveToStorage(newLogLeadTemp)

            // Update state
            set({ 
                logLeadTemp: newLogLeadTemp,
                error: null 
            })

            return true

        } catch (error) {
            console.error('Error updating log lead temp key error:', error)
            set({ 
                error: 'Failed to update log error status',
                isLoading: false 
            })
            return false
        }
    },

    addLogLeadByTemplatePipelineId: async (templatePipelineId: string, logLeadTemp: LogLeadTemp): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            const currentState = get().logLeadTemp
            const existingLogs = currentState[templatePipelineId] || []
            const newLogLeadTemp = {
                ...currentState,
                [templatePipelineId]: [...existingLogs, logLeadTemp]
            }

            await saveToStorage(newLogLeadTemp)

            set({
                logLeadTemp: newLogLeadTemp,
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error adding log lead temp:', error)
            set({
                error: 'Failed to add log lead temp',
                isLoading: false
            })
            return false
        }
    },

    updateLogLeadTempByIdLog: async (idLog: string, updatedLog: LogLeadTemp): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            const currentState = get().logLeadTemp
            const newLogLeadTemp: LogLeadTempStore = {}
            let found = false

            Object.keys(currentState).forEach((templatePipelineId) => {
                const logIndex = currentState[templatePipelineId].findIndex(
                    (log) => log.id === idLog
                )

                if (logIndex !== -1) {
                    newLogLeadTemp[templatePipelineId] = [
                        ...currentState[templatePipelineId].slice(0, logIndex),
                        updatedLog,
                        ...currentState[templatePipelineId].slice(logIndex + 1)
                    ]
                    found = true
                } else {
                    newLogLeadTemp[templatePipelineId] = [...currentState[templatePipelineId]]
                }
            })

            if (!found) {
                set({
                    error: `Log with pipelineId ${idLog} not found`,
                    isLoading: false
                })
                return false
            }

            await saveToStorage(newLogLeadTemp)

            set({
                logLeadTemp: newLogLeadTemp,
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error updating log lead temp:', error)
            set({
                error: 'Failed to update log lead temp',
                isLoading: false
            })
            return false
        }
    },

    removeLogLeadTempByIdLog: async (idLog: string): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            const currentState = get().logLeadTemp
            const newLogLeadTemp = { ...currentState }
            let found = false

            Object.keys(newLogLeadTemp).forEach((templatePipelineId) => {
                const originalLength = newLogLeadTemp[templatePipelineId].length
                newLogLeadTemp[templatePipelineId] = newLogLeadTemp[templatePipelineId].filter(
                    (log) => log.id !== idLog
                )

                if (newLogLeadTemp[templatePipelineId].length < originalLength) {
                    found = true
                }

                if (newLogLeadTemp[templatePipelineId].length === 0) {
                    delete newLogLeadTemp[templatePipelineId]
                }
            })

            if (!found) {
                set({
                    error: `Log with idLog ${idLog} not found`,
                    isLoading: false
                })
                return false
            }

            await saveToStorage(newLogLeadTemp)

            set({
                logLeadTemp: newLogLeadTemp,
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error removing log lead temp by pipelineId:', error)
            set({
                error: 'Failed to remove log lead temp',
                isLoading: false
            })
            return false
        }
    },

    removeLogLeadTempByLeadId: async (leadId: string): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            const currentState = get().logLeadTemp
            const newLogLeadTemp = { ...currentState }
            let found = false

            Object.keys(newLogLeadTemp).forEach((templatePipelineId) => {
                const originalLength = newLogLeadTemp[templatePipelineId].length
                newLogLeadTemp[templatePipelineId] = newLogLeadTemp[templatePipelineId].filter(
                    (log) => log.leadId !== leadId
                )

                if (newLogLeadTemp[templatePipelineId].length < originalLength) {
                    found = true
                }

                if (newLogLeadTemp[templatePipelineId].length === 0) {
                    delete newLogLeadTemp[templatePipelineId]
                }
            })

            if (!found) {
                set({
                    error: `Log with leadId ${leadId} not found`,
                    isLoading: false
                })
                return false
            }

            await saveToStorage(newLogLeadTemp)

            set({
                logLeadTemp: newLogLeadTemp,
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error removing log lead temp by leadId:', error)
            set({
                error: 'Failed to remove log lead temp',
                isLoading: false
            })
            return false
        }
    },

    removeLogLeadTempByTemplatePipelineId: async (templatePipelineId: string): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            const currentState = get().logLeadTemp

            if (!currentState[templatePipelineId]) {
                set({
                    error: `Template with id ${templatePipelineId} not found`,
                    isLoading: false
                })
                return false
            }

            const newLogLeadTemp = { ...currentState }
            delete newLogLeadTemp[templatePipelineId]

            await saveToStorage(newLogLeadTemp)

            set({
                logLeadTemp: newLogLeadTemp,
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error removing log lead temp by templatePipelineId:', error)
            set({
                error: 'Failed to remove log lead temp template',
                isLoading: false
            })
            return false
        }
    },

    clearAllLogLeadTemp: async (): Promise<boolean> => {
        try {
            set({ isLoading: true, error: null })

            await saveToStorage({})

            set({
                logLeadTemp: {},
                isLoading: false
            })

            return true
        } catch (error) {
            console.error('Error clearing all log lead temp:', error)
            set({
                error: 'Failed to clear all log lead temp',
                isLoading: false
            })
            return false
        }
    },

    getLogLeadTempByTemplatePipelineId: (templatePipelineId: string): LogLeadTemp[] => {
        return get().logLeadTemp[templatePipelineId] || []
    },

    getLatestLogLeadTempByLeadId: (leadId: string): LogLeadTemp | null => {
        const currentState = get().logLeadTemp

        for (const templatePipelineId in currentState) {
            const logArray = currentState[templatePipelineId]

            const newLogArray = logArray.filter((log) => log.leadId === leadId)
            const getLatestLogleadTemp = newLogArray[newLogArray.length - 1]

            if (getLatestLogleadTemp) {
                return getLatestLogleadTemp
            }

        }

        return null
    },

    getLengthLogSameLeadId: (leadId: string): number => {
        const currentState = get().logLeadTemp

        for (const templatePipelineId in currentState) {
            const logArray = currentState[templatePipelineId]

            const newLogArray = logArray.filter((log) => log.leadId === leadId)

            if (newLogArray.length > 0) {
                return newLogArray.length
            }
        }

        return 0
    },

    getNominalLogSameLeadId: (leadId: string): number => {
        const currentState = get().logLeadTemp
        let nominal = 0

        for (const templatePipelineId in currentState) {
            const logArray = currentState[templatePipelineId]

            const newLogArray = logArray.filter((log) => log.leadId === leadId)
            const latestLog = newLogArray[newLogArray.length - 1]

            if (latestLog) {
                nominal += latestLog.leadProducts.reduce((acc: number, curr: any) => acc + changeValueIfStringIntoNumber(curr.leadProductTotal), 0)
            }
        }

        return nominal
    },

    getLogLeadTempByLeadId: (leadId: string): LogLeadTemp | null => {
        const currentState = get().logLeadTemp

        for (const templatePipelineId in currentState) {
            const logLeadTemp = currentState[templatePipelineId].find((log) => log.leadId === leadId)

            if (logLeadTemp) {
                return logLeadTemp
            }
        }

        return null
    },

    getAllLogLeadTemp: (): LogLeadTempStore => get().logLeadTemp

}))

loadFromStorageHelper().then((initialData) => {
    // console.log("Initial data loaded:", initialData)
    useLogLeadTempStore.setState({ logLeadTemp: initialData })
}).catch((error) => {
    console.error('Error loading initial data:', error)
})

export default useLogLeadTempStore